/**
 * FilterBar Component
 * 放置在表格上方的过滤器区域（由子组件与 useFilterBar 组合而成）
 */

import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { InsightSearchField } from "./InsightSearchField";
import { useFilterBar } from "./useFilterBar";
import type { FilterBarProps } from "./FilterBar.types";
import { RegulationFilterPanel } from "./RegulationFilterPanel";
import type { SavedFilterChoice } from "./SavedFilterDialogs";
import { findNewlyCreatedUserFilter } from "./filterBarUtils";
import {
  emptyFilterSelection,
  listCriteriaToFilterSelection,
  mapFilterSelectionToListCriteria,
  type FilterSelectionState,
} from "./regulationFilterDraft";
import {
  applySavedFilterOrder,
  criteriaToSelection,
  deleteSavedFilterExtras,
  deleteSavedFilterNameOverride,
  readLocalSavedFilters,
  readSavedFilterExtras,
  readSavedFilterNameOverride,
  removeLocalSavedFilter,
  selectionToCriteria,
  updateLocalSavedFilter,
  writeSavedFilterExtras,
  writeSavedFilterNameOverride,
  writeSavedFilterOrder,
} from "./savedFilterSession";
import styles from "./FilterBar.module.css";
import { useHasPrivilege } from "../../../hooks/useHasPrivilege";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import {
  createUserFilter,
  deleteUserFilter,
  reorderUserFilters,
  updateUserFilter,
} from "../../../services/regulationsService";

export type { FilterBarProps } from "./FilterBar.types";

const EXTRA_KEYS = ["category", "sub_category", "topics"] as const;

function isSavedFilterApiSuccess(status: string | null | undefined, code: number): boolean {
  const normalized = status?.trim().toLowerCase();
  if (normalized === "failed" || normalized === "error") return false;
  return code === 200 || code === 201;
}

function savedFilterApiErrorMessage(error: unknown): string {
  const body = (error as { responseData?: { message?: string } } | null)?.responseData;
  const fromBody = body?.message?.trim();
  if (fromBody) return fromBody;
  if (error instanceof Error && error.message.trim()) return error.message.trim();
  return "Failed to save filter.";
}

function withExtraDimensions(
  base: FilterSelectionState,
  extras: FilterSelectionState,
): FilterSelectionState {
  const selections = { ...base.selections };
  for (const key of EXTRA_KEYS) {
    const options = extras.selections[key];
    if (options && options.length > 0) selections[key] = options;
    else delete selections[key];
  }
  return {
    selections,
    customFrom: base.customFrom,
    customTo: base.customTo,
  };
}

export const FilterBar: React.FC<FilterBarProps> = (props) => {
  const { onActiveSavedFilterIdChange, onFavoriteFilterChange } = props;
  const api = useFilterBar(props);
  const canReadSavedFilter = useHasPrivilege("gri.regulation.saved_filter");
  const { showMessage } = useSnackbar();
  const [panelExtras, setPanelExtras] = useState<FilterSelectionState>(
    emptyFilterSelection,
  );
  const [localVersion, setLocalVersion] = useState(0);

  const {
    savedFilters,
    activeFilterId,
    setActiveFilterId,
    loadSavedFilters,
    searchInput,
    setSearchInput,
    setHasFormBeenInteracted,
    handleMarketsChange,
    onProductTypesChange,
    onHAChange,
    onTagsChange,
    handleDateRangeChange,
    handleSearchKeyPress,
    handleSearchSubmit,
    handleSearchBlur,
    dismissShowFavoritePreset,
  } = api;

  const appliedFilterSelection = useMemo(
    () =>
      withExtraDimensions(
        listCriteriaToFilterSelection({
          markets: props.selectedMarkets,
          productTypes: props.selectedProductTypes,
          healthAuthorities: props.selectedHA,
          tags: props.selectedTags,
          fromDate: props.fromDate,
          toDate: props.toDate,
          favorite: props.favoriteFilter ?? null,
        }),
        panelExtras,
      ),
    [
      props.selectedMarkets,
      props.selectedProductTypes,
      props.selectedHA,
      props.selectedTags,
      props.fromDate,
      props.toDate,
      props.favoriteFilter,
      panelExtras,
    ],
  );

  const savedFilterChoices = useMemo<SavedFilterChoice[]>(() => {
    const fromApi = savedFilters.filter(() => localVersion >= 0)
      .filter((filter) => filter.id > 0)
      .map((filter) => ({
        id: filter.id,
        name: readSavedFilterNameOverride(filter.id) ?? filter.name,
        selection: criteriaToSelection(
          filter.criteria,
          readSavedFilterExtras(filter.id),
        ),
      }));
    const local = readLocalSavedFilters().map((filter) => ({
      id: filter.id,
      name: filter.name,
      selection: filter.selection,
    }));
    return applySavedFilterOrder([...fromApi, ...local]);
  }, [savedFilters, localVersion]);

  const handleApplyFilterSelection = (
    next: FilterSelectionState,
    savedFilterId: number | null,
  ) => {
    const mapped = mapFilterSelectionToListCriteria(next);
    setHasFormBeenInteracted(true);
    setPanelExtras(next);
    handleMarketsChange(null, mapped.markets);
    onProductTypesChange(mapped.productTypes);
    onHAChange(mapped.healthAuthorities);
    onTagsChange(mapped.tags);
    handleDateRangeChange(mapped.fromDate, mapped.toDate);
    onFavoriteFilterChange?.(mapped.favorite);
    setActiveFilterId(savedFilterId);
    dismissShowFavoritePreset();
  };

  const handleClearAppliedFilters = () => {
    setHasFormBeenInteracted(true);
    setPanelExtras(emptyFilterSelection());
    handleMarketsChange(null, []);
    onProductTypesChange([]);
    onHAChange([]);
    onTagsChange([]);
    handleDateRangeChange(null, null);
    onFavoriteFilterChange?.(null);
    setActiveFilterId(null);
    dismissShowFavoritePreset();
  };

  const handleCreateSavedFilter = async (
    name: string,
    selection: FilterSelectionState,
  ): Promise<{ error: string | null; id: number | null }> => {
    const trimmed = name.trim();
    if (!trimmed) return { error: "Please enter a filter name.", id: null };
    const duplicate = savedFilterChoices.some(
      (filter) => filter.name.trim().toLowerCase() === trimmed.toLowerCase(),
    );
    if (duplicate) {
      return { error: "A saved filter with this name already exists.", id: null };
    }
    const criteria = selectionToCriteria(selection);
    try {
      const previousIds = new Set(savedFilters.map((filter) => filter.id));
      const response = await createUserFilter({
        name: trimmed,
        criteria,
        is_favorite: false,
      });
      const message = response.message?.trim() ?? "";
      if (!isSavedFilterApiSuccess(response.status, response.code)) {
        if (message) showMessage(message, "error");
        return { error: message || "Failed to save filter.", id: null };
      }
      const latest = (await loadSavedFilters()) ?? [];
      const created = findNewlyCreatedUserFilter(latest, previousIds, trimmed);
      if (created) {
        writeSavedFilterExtras(created.id, selection);
        setLocalVersion((version) => version + 1);
      }
      if (message) showMessage(message, "success");
      return { error: null, id: created?.id ?? null };
    } catch (error) {
      const message = savedFilterApiErrorMessage(error);
      showMessage(message, "error");
      return { error: message, id: null };
    }
  };

  const handleUpdateSavedFilter = async (
    id: number,
    name: string,
    selection: FilterSelectionState,
  ): Promise<string | null> => {
    const trimmed = name.trim();
    if (!trimmed) return "Please enter a filter name.";
    const duplicate = savedFilterChoices.some(
      (filter) =>
        filter.id !== id &&
        filter.name.trim().toLowerCase() === trimmed.toLowerCase(),
    );
    if (duplicate) return "A saved filter with this name already exists.";
    writeSavedFilterExtras(id, selection);
    writeSavedFilterNameOverride(id, trimmed);
    if (id < 0) {
      updateLocalSavedFilter(id, trimmed, selection);
      setLocalVersion((version) => version + 1);
      showMessage(`Saved filter "${trimmed}" updated successfully.`, "success");
      return null;
    }
    try {
      const response = await updateUserFilter(id, {
        name: trimmed,
        criteria: selectionToCriteria(selection),
      });
      if (response.code === 200 || response.code === 201) {
        await loadSavedFilters();
      }
    } catch {
      // Extras already stored, so the open panel still shows the edit.
    }
    setLocalVersion((version) => version + 1);
    showMessage(`Saved filter "${trimmed}" updated successfully.`, "success");
    return null;
  };

  const handleDeleteSavedFilter = async (id: number) => {
    deleteSavedFilterExtras(id);
    deleteSavedFilterNameOverride(id);
    removeLocalSavedFilter(id);
    if (id > 0) {
      try {
        await deleteUserFilter(id);
        await loadSavedFilters();
      } catch {
        // The row is already gone from the session overlay.
      }
    }
    if (activeFilterId === id) setActiveFilterId(null);
    setLocalVersion((version) => version + 1);
    showMessage("Saved filter deleted successfully.", "success");
  };

  const handleReorderSavedFilters = (orderedIds: number[]) => {
    writeSavedFilterOrder(orderedIds);
    setLocalVersion((version) => version + 1);
    const remoteIds = orderedIds.filter((id) => id > 0);
    if (remoteIds.length === 0) return;
    void reorderUserFilters(remoteIds).then(() => loadSavedFilters()).catch(() => {
      // Session order still drives the list.
    });
  };

  useEffect(() => {
    onActiveSavedFilterIdChange?.(activeFilterId);
  }, [activeFilterId, onActiveSavedFilterIdChange]);

  return (
    <Box
      className={styles.filterBar}
      sx={{
        px: 2,
        py: 1.5,
        borderRadius: 1,
        borderTop: "none",
        borderRight: 1,
        borderBottom: 1,
        borderLeft: "none",
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: "none",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <InsightSearchField
          value={searchInput}
          onChange={(value) => {
            setSearchInput(value);
            setHasFormBeenInteracted(true);
          }}
          onKeyDown={handleSearchKeyPress}
          onBlur={handleSearchBlur}
          onClear={() => {
            setSearchInput("");
            handleSearchSubmit("");
            setHasFormBeenInteracted(true);
          }}
          />
        </Box>
        <RegulationFilterPanel
          applied={appliedFilterSelection}
          appliedSavedFilterId={
            activeFilterId != null && activeFilterId > 0 ? activeFilterId : null
          }
          savedFilters={savedFilterChoices}
          canManageSavedFilters={canReadSavedFilter}
          onApply={handleApplyFilterSelection}
          onClearApplied={handleClearAppliedFilters}
          onCreateSavedFilter={handleCreateSavedFilter}
          onUpdateSavedFilter={handleUpdateSavedFilter}
          onDeleteSavedFilter={handleDeleteSavedFilter}
          onReorderSavedFilters={handleReorderSavedFilters}
        />
        {props.trailing ? (
          <Box sx={{ flex: "none", display: "flex", alignItems: "center" }}>
            {props.trailing}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
